import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}

export default function Comments({ checkpointId }) {
  const { user, login } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    const t = localStorage.getItem('logos_session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };

  useEffect(() => {
    fetch(`${API}/checkpoints/${checkpointId}/comments`)
      .then(r => r.ok ? r.json() : [])
      .then(setComments)
      .finally(() => setLoading(false));
  }, [checkpointId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/checkpoints/${checkpointId}/comments`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ body: text })
      });
      if (res.ok) {
        const c = await res.json();
        setComments(prev => [...prev, c]);
        setBody('');
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'failed to post');
      }
    } catch { setError('failed to post'); }
    finally { setSubmitting(false); }
  };

  const remove = async (comment_id) => {
    const prev = comments;
    setComments(comments.filter(c => c.comment_id !== comment_id));
    const res = await fetch(`${API}/comments/${comment_id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders()
    });
    if (!res.ok) setComments(prev);
  };

  const canDelete = (c) => user && (user.user_id === c.user_id || user.role === 'admin');

  return (
    <section className="mt-12 pt-10 border-t border-[#1e251f]" data-testid="comments-section">
      <h2 className="text-xl font-bold text-white lowercase mb-6">
        {comments.length > 0 ? `${comments.length} comment${comments.length !== 1 ? 's' : ''}` : 'comments'}
      </h2>

      {/* Compose */}
      {user ? (
        <form onSubmit={submit} className="mb-8" data-testid="comment-compose">
          <textarea
            value={body}
            onChange={e => setBody(e.target.value.slice(0, 2000))}
            placeholder="say something."
            rows={3}
            className="w-full bg-[#0e120f] border border-[#272e28] text-white lowercase px-4 py-3 rounded-lg outline-none focus:border-[#4a514a] transition-colors resize-none text-sm"
            data-testid="comment-body"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[#333333] text-xs lowercase">{body.length}/2000</span>
            <button
              type="submit"
              disabled={!body.trim() || submitting}
              className="border border-[#383f38] px-5 py-1.5 text-white lowercase text-sm hover:bg-white hover:text-black transition-all duration-300 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="comment-submit"
            >
              {submitting ? 'posting…' : 'post'}
            </button>
          </div>
          {error && <p className="text-white/70 lowercase text-sm mt-2">{error}</p>}
        </form>
      ) : (
        <div className="mb-8 border border-[#202721] rounded-lg p-4 flex items-center justify-between">
          <span className="text-[#888888] lowercase text-sm">log in to comment.</span>
          <button onClick={login} className="text-white lowercase text-sm hover:underline underline-offset-4">login</button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-[#555555] lowercase text-sm animate-pulse">loading…</p>
      ) : comments.length === 0 ? (
        <p className="text-[#555555] lowercase text-sm">nothing yet.</p>
      ) : (
        <ul className="space-y-5" data-testid="comment-list">
          {comments.map(c => (
            <li key={c.comment_id} className="flex items-start gap-3" data-testid={`comment-${c.comment_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 text-sm">
                  <Link to={`/u/${c.username}`} className="text-white font-bold lowercase hover:underline underline-offset-4 decoration-[#444444]">
                    {c.username}
                  </Link>
                  {c.verified && <img src="/phi-logo-64.png" alt="verified" title="verified" className="verified-badge" />}
                  <span className="text-[#555555] lowercase text-xs tabular-nums">{timeAgo(c.created_at)}</span>
                  {canDelete(c) && (
                    <button
                      onClick={() => remove(c.comment_id)}
                      className="text-[#333333] lowercase text-xs hover:text-white transition-colors ml-auto"
                      data-testid={`delete-comment-${c.comment_id}`}
                    >
                      delete
                    </button>
                  )}
                </div>
                <p className="text-[#cccccc] lowercase text-sm mt-1 whitespace-pre-wrap break-words">{c.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
